import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Download, Search, Filter } from 'lucide-react';

export default function BagSize() {
  const [bagSizeRecords, setBagSizeRecords] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  const [search, setSearch] = useState('');
  const [selectedCommodity, setSelectedCommodity] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [role, setRole] = useState('');

  const [form, setForm] = useState({
    id: null,
    commodityId: '',
    seasonId: '',
    bag_size: '',
  });

  const token = localStorage.getItem('token');

  const fetchMasterData = async () => {
    try {
      const [commRes, seasonRes] = await Promise.all([
        axios.get('commodity'),
        axios.get('seasons?paginate=false'),
      ]);
      setCommodities(
        commRes.data.commodities || commRes.data.data || (Array.isArray(commRes.data) ? commRes.data : [])
      );
      setSeasons(
        seasonRes.data.seasons || seasonRes.data.data || (Array.isArray(seasonRes.data) ? seasonRes.data : [])
      );
    } catch (err) {
      console.error('Error fetching master data:', err);
    }
  };

  const fetchBagSizes = async (
    pageNum = 1,
    limitNum = 10,
    searchTerm = '',
    commodityId = '',
    seasonId = ''
  ) => {
    try {
      setLoading(true);
      let url = `bag-size?page=${pageNum}&limit=${limitNum}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (commodityId) url += `&commodity_id=${encodeURIComponent(commodityId)}`;
      if (seasonId) url += `&season_id=${encodeURIComponent(seasonId)}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBagSizeRecords(res.data.bagSizes || res.data.data || []);
      setPage(res.data.page || pageNum);
      setLimit(res.data.limit || limitNum);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching Bag Sizes:', err);
      setBagSizeRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setRole(user?.roles?.[0]?.name || '');
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (fetchDataRef.current) {
      clearTimeout(fetchDataRef.current);
    }

    fetchDataRef.current = setTimeout(() => {
      fetchBagSizes(page, limit, search, selectedCommodity, selectedSeason);
    }, 300);

    return () => {
      if (fetchDataRef.current) {
        clearTimeout(fetchDataRef.current);
      }
    };
  }, [page, limit, search, selectedCommodity, selectedSeason]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleCommodityFilter = (e) => {
    setSelectedCommodity(e.target.value);
    setPage(1);
  };

  const handleSeasonFilter = (e) => {
    setSelectedSeason(e.target.value);
    setPage(1);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAdd = () => {
    setForm({ id: null, commodityId: '', seasonId: '', bag_size: '' });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      commodityId: item.commodity?.id || '',
      seasonId: item.season?.id || '',
      bag_size: item.bag_size,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const handleExecuteDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      await axios.delete(`bag-size/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('✅ Bag size record deleted successfully!');
      setIsDeleteModalOpen(false);
      setDeleteId(null);
      fetchBagSizes(page, limit, search, selectedCommodity, selectedSeason);
    } catch (err) {
      console.error('Delete error:', err);
      alert('❌ Failed to delete bag size record');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.commodityId || !form.seasonId || !form.bag_size) {
      setFormError('All fields are required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        commodity_id: form.commodityId,
        season_id: form.seasonId,
        bag_size: Number(form.bag_size),
      };

      if (form.id) {
        await axios.patch(`bag-size/${form.id}`, payload);
        alert('✅ Bag size updated successfully!');
      } else {
        await axios.post('bag-size', payload);
        alert('✅ Bag size added successfully!');
      }
      setIsModalOpen(false);
      fetchBagSizes(page, limit, search, selectedCommodity, selectedSeason);
    } catch (err) {
      console.error('Save error:', err);
      setFormError(
        err?.response?.data?.message || 'Failed to save bag size record'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      let url = `bag-size/excel/download?`;
      if (selectedCommodity) url += `commodity_id=${encodeURIComponent(selectedCommodity)}&`;
      if (selectedSeason) url += `season_id=${encodeURIComponent(selectedSeason)}&`;

      const res = await axios.get(url, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `bag_sizes_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
      alert('❌ Failed to export bag size records');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-6 md:p-10 text-gray-900">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-800 tracking-tight">
            Bag Size Management
          </h2>
          <p className="text-gray-500 mt-1">Manage commodity and season bag sizes here</p>
        </div>

        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          {role !== 'User' && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAdd}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 transition text-white px-4 py-2.5 rounded-lg shadow-md flex-1 md:flex-none"
            >
              <Plus size={18} />
              <span>Add New</span>
            </motion.button>
          )}

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExport}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 transition text-white px-4 py-2.5 rounded-lg shadow-md flex-1 md:flex-none"
          >
            <Download size={18} />
            <span>Export Excel</span>
          </motion.button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by commodity or season..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
            value={search}
            onChange={handleSearch}
          />
        </div>

        {/* Commodity Filter */}
        <div className="relative">
          <select
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white text-gray-700"
            value={selectedCommodity}
            onChange={handleCommodityFilter}
          >
            <option value="">All Commodities</option>
            {commodities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.commodityname}
              </option>
            ))}
          </select>
        </div>

        {/* Season Filter */}
        <div className="relative">
          <select
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white text-gray-700"
            value={selectedSeason}
            onChange={handleSeasonFilter}
          >
            <option value="">All Seasons</option>
            {seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Commodity</th>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Season</th>
                <th className="py-4 px-6 text-sm font-semibold text-gray-600 uppercase tracking-wider">Bag Size (Kg)</th>
                {role !== 'User' && (
                  <th className="py-4 px-6 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={role !== 'User' ? 4 : 3} className="py-10 text-center text-gray-500">
                    Loading bag sizes...
                  </td>
                </tr>
              ) : bagSizeRecords.length > 0 ? (
                bagSizeRecords.map((item) => (
                  <tr key={item.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                    <td className="py-4 px-6 text-sm font-medium text-gray-800">{item.commodity?.commodityname}</td>
                    <td className="py-4 px-6 text-sm text-gray-700">{item.season?.name}</td>
                    <td className="py-4 px-6 text-sm font-bold text-blue-600">{item.bag_size} Kg</td>
                    {role !== 'User' && (
                      <td className="py-4 px-6 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleEdit(item)}
                            className="text-blue-600 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50 transition"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeleteConfirm(item.id)}
                            className="text-red-600 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </motion.button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={role !== 'User' ? 4 : 3} className="py-10 text-center text-gray-500">
                    No bag size records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 transition font-medium text-gray-700"
          >
            Previous
          </button>
          <span className="text-gray-600 font-medium">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 transition font-medium text-gray-700"
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <div className="flex justify-between items-center bg-gray-50 px-6 py-4 border-b">
                <h3 className="text-xl font-bold text-gray-800">
                  {form.id ? 'Edit Bag Size Record' : 'Add New Bag Size'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100 font-medium">
                    {formError}
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Commodity</label>
                  <select
                    name="commodityId"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                    value={form.commodityId}
                    onChange={handleChange}
                  >
                    <option value="">Select Commodity</option>
                    {commodities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.commodityname}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Season</label>
                  <select
                    name="seasonId"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                    value={form.seasonId}
                    onChange={handleChange}
                  >
                    <option value="">Select Season</option>
                    {seasons.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">Bag Size (Kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="bag_size"
                    placeholder="Enter bag size in Kg"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
                    value={form.bag_size}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 shadow-md transition disabled:opacity-70"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {isDeleteModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden p-6 text-center"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-2">Delete Bag Size</h3>
              <p className="text-gray-600 mb-6 text-sm">
                Are you sure you want to delete this bag size record? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setDeleteId(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExecuteDelete}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 shadow-md transition disabled:opacity-70"
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
